import React, { useCallback } from 'react';

type TRef = React.RefObject<HTMLElement>;

interface IScrollSettings {
    alignment?: ScrollLogicalPosition,
    behavior?: ScrollBehavior,
    absolute?: 'top' | 'bottom',
}

/**
 * Хук скролла к элементу
 *
 * Если нужен просто скролл (вверх/вниз страницы) без привязки к элементу, используйте настройку { absolute: 'top' | 'bottom' }
 *
 * @function
 * @name hooks.useScroll
 * @param {Object} settings - Настройки поведения скролла. С аргументом offsetY отработает только свойство settings.behavior.
 * @param {number} offsetY - Скроллинг со сдвигом позиции.
 * @returns {(ref: TRef) => void}
 */
export function useScroll(
    settings?: IScrollSettings,
    offsetY?: number,
): (ref: TRef | null) => void {
    return useCallback((ref) => {
        const behavior = settings?.behavior || 'smooth';

        if (settings?.absolute) {
            window.scrollTo({ top: settings.absolute === 'top' ? 0 : document.body.scrollHeight, behavior });
            return;
        }

        if (offsetY) {
            const top = ref?.current?.getBoundingClientRect().top || 0;
            const y = top + window.pageYOffset + offsetY;
            window.scrollTo({ top: y, behavior });
        } else {
            ref?.current?.scrollIntoView({
                behavior,
                block: settings?.alignment || 'center',
            });
        }
    }, [settings, offsetY]);
}
